import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Box, Grid, GridSize, MenuItem, Typography } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import {
  ConfigFieldControl,
  ConfigTextField,
  IconPicker
} from '@sierralabs/core-ui';
import { useFindEntityDataById } from '@sierralabs/entity-ui';
import {
  SiteComponent,
  SiteComponentConfigProps,
  SiteComponentPlugin,
  SiteComponentProps,
  SiteTaskStatusCardConfigurator,
  useConfigComponent,
  useSiteContext,
  useSubscribeComponentChanges
} from '@sierralabs/site-ui';
import {
  TaskEntity,
  TaskEvent,
  TaskEventType,
  useFindTask
} from '@sierralabs/workflow-ui';

import { useJiraConnectContext } from '../secure-portal/jira-connect/JiraConnect.context';
import TaskJiraV2AssignCard from '../secure-portal/task-jira-v2/cards/TaskJiraV2AssignCard';

const StyledAlertTitle = styled(AlertTitle)`
  font-size: 15px;
  font-weight: 600;
`;

export interface TaskJiraAssignCardProps {
  icon?: string;
  title?: string;
  entityId?: string;
  entityTable?: string;
  routeId?: string;
  routeParam?: string;
  grid?: {
    xs?: GridSize;
    sm?: GridSize;
    md?: GridSize;
    lg?: GridSize;
    xl?: GridSize;
  };
}

export const TaskJiraAssignCard: React.FC<
  SiteComponentProps<TaskJiraAssignCardProps>
> = props => {
  const component = useSubscribeComponentChanges(
    TaskJiraAssignCardPlugin,
    props
  );
  const { editing, isHover, hoverProps } = useSiteContext();
  const classes = ['task-jira-assign-card'];
  if (isHover(component)) {
    classes.push('component-hover');
  }
  const {
    icon,
    title,
    entityId,
    entityTable,
    routeParam,
    grid = { xs: 12 }
  } = component.props || {};
  const params = useParams<Record<string, any>>();
  const dataId = routeParam ? params[routeParam] : undefined;
  const { isAvailable } = useJiraConnectContext();
  const { entityData, getEntityDataById } = useFindEntityDataById<TaskEntity>(
    entityId || entityTable || '',
    dataId
  );
  const taskId = entityData?.taskId || 0;
  const { task, findTask } = useFindTask(taskId);
  const showAlert = !isAvailable && editing;

  useEffect(() => {
    getEntityDataById();
  }, [getEntityDataById]);

  useEffect(() => {
    taskId && findTask();
    // refresh on change status
    return TaskEvent.handle(
      TaskEventType.ChangeStatus,
      () => taskId && findTask()
    );
  }, [taskId, findTask]);

  return (
    <SiteComponent plugin={TaskJiraAssignCardPlugin} {...props}>
      {(isAvailable || showAlert) && (
        <Grid
          item
          xs={grid.xs}
          sm={grid.sm}
          md={grid.md}
          lg={grid.lg}
          className={classes.join(' ')}
          {...hoverProps(component)}
        >
          {showAlert ? (
            <Alert severity='warning'>
              <StyledAlertTitle>
                JIRA Connect API is not available
              </StyledAlertTitle>
              This component cannot be displayed properly. Please open SierraQMS
              within JIRA and try again.
            </Alert>
          ) : isAvailable ? (
            <TaskJiraV2AssignCard
              task={task}
              icon={icon}
              title={title}
              entity={entityData as any}
              className={classes.join(' ')}
              cardProps={hoverProps(component) as any}
            />
          ) : null}
        </Grid>
      )}
    </SiteComponent>
  );
};

export const TaskJiraAssignCardConfigurator: React.FC<
  SiteComponentConfigProps<TaskJiraAssignCardProps>
> = props => {
  const { component, update } = useConfigComponent(props.component);
  return (
    <>
      <ConfigFieldControl>
        <IconPicker
          value={component.props?.icon || ''}
          onChange={icon => update('props.icon', icon)}
        />
        <ConfigTextField
          fullWidth
          label='Title'
          value={component.props?.title || ''}
          onChange={event => update('props.title', event.target.value)}
        />
      </ConfigFieldControl>
      <SiteTaskStatusCardConfigurator {...props} />
      <Box mt={1}>
        <Typography variant='overline'>Grid</Typography>
      </Box>
      {(['xs', 'sm', 'md', 'lg'] as const).map(size => (
        <ConfigFieldControl key={size}>
          <ConfigTextField
            fullWidth
            type='number'
            label={size.toUpperCase()}
            value={component.props?.grid?.[size] || ''}
            onChange={event => {
              if (!component.props?.grid) {
                update('props.grid', {});
              }
              const value = event.target.value;
              update(
                `props.grid.${size}`,
                value === 'auto' ? 'auto' : parseInt(value)
              );
            }}
          >
            {['auto', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(spacing => (
              <MenuItem key={spacing} value={spacing}>
                {spacing}
              </MenuItem>
            ))}
          </ConfigTextField>
        </ConfigFieldControl>
      ))}
    </>
  );
};

export const TaskJiraAssignCardPlugin: SiteComponentPlugin<TaskJiraAssignCardProps> =
  {
    name: 'Task Jira Assign Card',
    type: 'task-jira-assign-card',
    component: TaskJiraAssignCard,
    configurator: TaskJiraAssignCardConfigurator,
    initConfig: component => {
      if (!component.props) component.props = {};
      component.props.grid = { xs: 12 };
      component.props.icon = 'confirmation_number';
      component.props.title = 'Linked Jira Issues';
      return component;
    }
  };
